// File: ProtectedRoute.jsx
//
// The "bouncer" that sits in front of pages that require sign-in.
// App.js wraps a page in <ProtectedRoute> when that page should not
// be visible to logged-out users.
//
// What it does, in plain English:
//   1. Check if anyone is signed in. If not -> send them to /.
//   2. Check if that user has a profile saved in our database.
//      If not -> send them to /home (the Home page handles new users).
//   3. If the route was marked "requireAdmin" -> make sure the user
//      has staff = true. If not -> send them to /home.
//   4. Otherwise -> let them through and show the page.
//
// To require admin on a route, pass requireAdmin to the wrapper, e.g.
//   <ProtectedRoute requireAdmin><Admin /></ProtectedRoute>

import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { auth } from "./firebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";

const db = getFirestore();

export default function ProtectedRoute({ children, requireAdmin = false }) {
  // We don't know who the user is yet — start with `null` so we can
  // tell "still checking" apart from "definitely not allowed".
  const [allowed, setAllowed] = useState(null);
  const [signedIn, setSignedIn] = useState(null);

  // Watch for sign-in / sign-out changes and re-check access.
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      try {
        // Nobody is signed in -> block.
        if (!u) {
          setSignedIn(false);
          setAllowed(false);
          return;
        }

        setSignedIn(true);

        // Look up the user's profile in the database.
        const ref = doc(db, "users", u.uid);
        const snap = await getDoc(ref);

        // No profile yet -> block (they need to finish sign-up first).
        if (!snap.exists()) {
          setAllowed(false);
          return;
        }

        const d = snap.data() || {};
        const isAdmin = d.staff === true;

        // For normal routes, just having a profile is enough.
        // For admin routes, the user must be staff.
        const finalAllowed = requireAdmin ? isAdmin : true;

        setAllowed(finalAllowed);
      } catch (e) {
        console.error("ProtectedRoute failed:", e);
        setSignedIn(false);
        setAllowed(false);
      }
    });

    return () => unsub();
  }, [requireAdmin]);

  // Still loading -> show nothing for a moment.
  if (allowed === null || signedIn === null) return null;

  // Not signed in -> send to the login screen.
  if (!signedIn) return <Navigate to="/" replace />;

  // Signed in but not allowed (e.g. not admin) -> send to Home.
  if (!allowed) return <Navigate to="/home" replace />;

  // All good -> show the page.
  return children;
}
